import { makeAutoObservable } from "mobx";
import { log } from "./lib/log";
import { RootStore } from "./store";

/**
 * This store is responsible for preloading additional content assets for the tour.
 */
export class AssetManager {
  cache = new Map<string, string>();
  queue: string[] = [];
  isLoading = false;

  constructor(public rootStore: RootStore) {
    makeAutoObservable(this);
  }

  init() {
    this.rootStore.tourLifecycleManager.events.on("tourStarted", () => {
      this.clearCache();
      this.preloadTourStepAssets();
    });
    this.rootStore.tourLifecycleManager.events.on("tourFinished", () => this.clearCache());
    this.rootStore.tourLifecycleManager.events.on("tourTerminated", () => this.clearCache());
  }

  getAsset(url: string) {
    if (this.rootStore.pyneSettings.skipBlobMediaPreload) {
      return { url, cached: false };
    }

    let objectUrl: string | undefined = undefined;
    if (this.cache.has(url)) {
      objectUrl = this.cache.get(url)!;
    }
    return objectUrl ? { url: objectUrl, cached: true } : { url, cached: false };
  }

  private preloadTourStepAssets() {
    log("AssetManager:", "preloading tour step assets");

    if (!this.rootStore.currentTour) return;
    const tour = this.rootStore.currentTour;

    const urls = [];
    for (const step of tour.nextSteps) {
      for (const asset of step.assetUrls) {
        urls.push(asset.src);
      }
    }

    this.queue.push(...urls);
    this.loadAssets();
  }

  clearCache() {
    Object.values(this.cache).forEach((url) => URL.revokeObjectURL(url));
    this.cache.clear();
  }

  private async loadAssets() {
    if (this.isLoading) return;
    this.isLoading = true;
    while (this.queue.length) {
      const url = this.queue.shift()!;
      try {
        await this.fetch(url);
      } catch (e) {
        console.error(e);
      }
    }
    this.isLoading = true;
  }

  private async fetch(url: string) {
    if (this.cache.has(url)) return;
    const response = await fetch(url);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    this.cache.set(url, objectUrl);
    log("AssetManager:", "prefetched", url);
  }
}
